<template>
    <div>
        <GoBackButtonComponent />
    </div>
    
    <section class="m-auto px-2 md:px-0 text-secondary w-full md:w-[30rem] md:max-w-[30rem]">
        <LoadingDialog :visible="loading" />
        <div class="rounded shadow p-3 text-center bg-white">
            <LogoComponent :theme="'dark'" :customClass="'text-4xl md:text-6xl'" /> 
            <p class="text-2xl font-bold py-4">
                {{ translate('Welcome!') }}
            </p>
            <p>
                {{ translate('Enter your email and password to log in to your account.') }}
            </p>
            <InputText 
                v-model="authForm.email"
                :style="getStyle('email')"
                class="w-full py-2 px-3 rounded-lg border border-color-iron mt-5 border-focus-purple-suit"
                type="text"
                :placeholder="translate('email', 'ucfirst')"
            />
            <Password 
                v-model="authForm.password"
                class="mt-5 w-full"
                :input-style="getStyle('password')"
                :input-class="'w-full py-2 px-3 rounded-lg border border-color-iron border-focus-purple-suit'"
                :placeholder="translate('password', 'ucfirst')"
                :feedback="false"
                toggleMask 
            />
            <div class="flex justify-between">
                <p class="mt-4 flex cursor-pointer">
                    <Checkbox 
                        class="my-auto mr-2 rounded" 
                        v-model="authForm.remember" 
                        :binary="true" 
                        :input-id="remember" 
                        :class="getClassForRemember()" 
                        :input-class="getClassForRemember()"
                    />
                    <span class="my-auto" for="remember" @click="authForm.remember = !authForm.remember">
                        {{ translate('Remember password') }}
                    </span>
                </p>
                <router-link :to="'/forgot-password'" class="mt-4 flex cursor-pointer">
                    {{ translate('I forgot my password.') }}
                </router-link>
            </div>
            <Button 
                class="mt-5 w-full bg-purple-suit-forced text-white p-1"
                :label="translate('enter', 'ucfirst')"
                @click.prevent="login()"
            />
            <p class="mt-5 fill-text-line text-secondary opacity-50">
                {{ translate("Don't have an account yet?") }}
            </p>
            <router-link :to="'/register'">
                <Button 
                    class="no-bg-button mt-5 w-full p-2 rounded"
                    unstyled
                    :label="translate('create account', 'ucfirst')"
                />
            </router-link>
        </div>
    </section>
</template>
<script setup>
    import { onMounted, onUnmounted, ref } from 'vue';
    import InputText from 'primevue/inputtext';
    import Password from 'primevue/password';
    import Checkbox from 'primevue/checkbox';
    import Button from 'primevue/button';
    import { showToast } from '@/composables/toast';
    import { useToast } from 'primevue/usetoast';
    import { sleep } from '@/composables/sleep';
    import LoadingDialog from '@/components/Utils/LoadingDialog.vue';
    import authUserService from '@/services/AuthUserService';
    import { isScreeSmall } from '@/composables/windowSize';
    import { useAuthStore } from '@/stores/auth';
    import urlHandler from '@/composables/urlHandler';
    import { useContextStore } from '@/stores/context';
    import GoBackButtonComponent from '@/components/misc/GoBackButtonComponent.vue';
    import LogoComponent from '@/components/LogoComponent.vue';
    import { translate } from '@/composables/translate';

    const authStore = useAuthStore();
    const contextStore = useContextStore();
    const breakPoint = 500;
    const toast = useToast();
    let loading = ref(false);
    let authForm = ref({
        email: '',
        password: '',
        remember: false
    }) 

    onMounted(() => {
        if (authStore.isAuthenticated) {
            return urlHandler.redirectAndScroll('/dashboard');
        }

        window.addEventListener('resize', updateScreenSize);
        updateScreenSize();
    });

    onUnmounted(() => {
      window.removeEventListener('resize', updateScreenSize);
    });

    const classToUse = ref('');
    const screenSize = ref(window.innerWidth);
    const updateScreenSize = () => {
        screenSize.value = window.innerWidth;
        classToUse.value = isScreeSmall(breakPoint) ? 'w-full' : 'm-form-w';
    };

    function getStyle(type = 'email'){
        let notFilledStyle = {
            'background-color': '#e3e3e3',
            'color': '#fff'
        }
        if(authForm.value[type] != undefined && authForm.value[type].length == 0)
            return notFilledStyle;
        return {};
    }

    function getClassForRemember(){
        return authForm.value.remember ? 'bg-purple-suit-forced' : '';
    }

    async function login(){
        loading.value = true;
        const response = await authUserService.login(authForm.value);
        loading.value = false;

        if(!response.success()){
            if (response.getCode() == response.definedCodes.NOT_VERIFIED) {
                urlHandler.redirectAndScroll('/email/verify', { action: 'resend', email: authForm.value.email });
                return;
            }

            showToast(toast, response.getMessage(), 'error');
            return;
        }
        showToast(toast, translate('Login successful, welcome'));            
        await sleep(500);

        authStore.login(response.getContent().content);

        let requestedUrl = contextStore.getRequestedRoute;
        let requestedParams = contextStore.getRequestedRouteParams;

        if (['/email/verify', '/reset-password', '/login', '/register', null, ''].includes(requestedUrl)) {
            requestedUrl = authStore.isAdmin ? '/admin/dashboard' : '/dashboard';
            requestedParams = {};
        }

        urlHandler.redirectAndScroll(requestedUrl, requestedParams);

        contextStore.saveIgnoreIncomingRoute(false);
    }

    window
</script>